import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import styles from './ProjectTemplate.module.css';
import API from '../api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const ProjectTemplate = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState({ projectName: '', client: '', year: '', description: '', solution: '', industry: '', timeline: '', keyResults: [], deliverables: [], teamMembers: [] });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const res = await API.get(`/projects/${id}`);
      setProject(res.data);
      setDraft({
        projectName: res.data.projectName || '',
        client: res.data.client || '',
        year: res.data.year || '',
        description: res.data.description || '',
        solution: res.data.solution || '',
        industry: res.data.industry || '',
        timeline: res.data.timeline || '',
        keyResults: Array.isArray(res.data.keyResults) ? res.data.keyResults : [],
        deliverables: Array.isArray(res.data.deliverables) ? res.data.deliverables : [],
        teamMembers: Array.isArray(res.data.teamMembers) ? res.data.teamMembers : []
      });
      setError(null);
    } catch (e) {
      setError('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const saveProject = async () => {
    try {
      const payload = {
        projectName: draft.projectName,
        client: draft.client,
        year: draft.year,
        description: draft.description,
        solution: draft.solution,
        industry: draft.industry,
        timeline: draft.timeline,
        keyResults: draft.keyResults,
        deliverables: draft.deliverables,
        teamMembers: draft.teamMembers,
      };
      const res = await API.put(`/projects/${id}`, payload);
      setProject(res.data);
      toast.success('Project saved');
      setEditMode(false);
    } catch (e) {
      toast.error('Failed to save project');
    }
  };

  const addKeyResult = () => {
    setDraft((d) => ({ ...d, keyResults: [...(d.keyResults || []), ''] }));
  };
  const updateKeyResult = (idx, value) => {
    setDraft((d) => {
      const next = [...(d.keyResults || [])];
      next[idx] = value;
      return { ...d, keyResults: next };
    });
  };
  const removeKeyResult = (idx) => {
    setDraft((d) => ({ ...d, keyResults: (d.keyResults || []).filter((_, i) => i !== idx) }));
  };

  const addDeliverable = () => {
    setDraft((d) => ({ ...d, deliverables: [...(d.deliverables || []), ''] }));
  };
  const updateDeliverable = (idx, value) => {
    setDraft((d) => {
      const next = [...(d.deliverables || [])];
      next[idx] = value;
      return { ...d, deliverables: next };
    });
  };
  const removeDeliverable = (idx) => {
    setDraft((d) => ({ ...d, deliverables: (d.deliverables || []).filter((_, i) => i !== idx) }));
  };

  const addTeamMember = () => {
    setDraft((d) => ({ ...d, teamMembers: [...(d.teamMembers || []), { fullName: '', role: '' }] }));
  };
  const updateTeamMember = (idx, key, value) => {
    setDraft((d) => {
      const next = [...(d.teamMembers || [])];
      next[idx] = { ...next[idx], [key]: value };
      return { ...d, teamMembers: next };
    });
  };
  const removeTeamMember = (idx) => {
    setDraft((d) => ({ ...d, teamMembers: (d.teamMembers || []).filter((_, i) => i !== idx) }));
  };

  const handleThumbnailChange = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      const form = new FormData();
      form.append('image', file);
      const res = await API.put(`/projects/${id}`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProject(res.data);
      toast.success('Thumbnail updated');
    } catch (e) {
      toast.error('Failed to update thumbnail');
    } finally {
      setUploading(false);
    }
  };

  const handleAddVisual = async (file) => {
    if (!file) return;
    try {
      if (file.size > 25 * 1024 * 1024) {
        toast.error('File too large. Max 25MB.');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Only image files are allowed');
        return;
      }
      setUploading(true);
      const form = new FormData();
      form.append('image', file);
      const res = await API.post(`/projects/${id}/visuals`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProject(res.data);
      toast.success('Visual added');
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to add visual';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveVisual = async (imageUrl) => {
    try {
      const res = await API.delete(`/projects/${id}/visuals`, { data: { imageUrl } });
      setProject(res.data);
      toast.success('Visual removed');
    } catch (e) {
      toast.error('Failed to remove visual');
    }
  };

  const backendOrigin = String(API.defaults.baseURL || '')
    .replace(/\/$/, '')
    .replace(/\/api$/, '');
  const resolveUrl = (u) => (u && !/^https?:/i.test(u) ? `${backendOrigin}${u}` : u);

  return (
    <div className={styles.pageContainer}>
      <Header />
      <main className={styles.main} aria-labelledby="project-title">
        <div className={styles.container}>
          {loading ? (
            <p>Loading...</p>
          ) : error ? (
            <p>{error}</p>
          ) : !project ? (
            <p>Not found</p>
          ) : (
            <>
              <header className={styles.projectHeader}>
                {isAdmin && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                    <button onClick={() => setEditMode(!editMode)}>
                      {editMode ? 'Done' : 'Edit'}
                    </button>
                    {editMode && <button onClick={saveProject}>Save</button>}
                  </div>
                )}
                {editMode ? (
                  <input
                    aria-label="Project name"
                    value={draft.projectName}
                    onChange={(e) => setDraft({ ...draft, projectName: e.target.value })}
                    style={{ fontSize: '1.5em', width: '100%', padding: 8, margin: '12px 0' }}
                  />
                ) : (
                  <h1 id="project-title">{project.projectName}</h1>
                )}
              </header>

              <section className={styles.mainContent}>
                <div className={styles.leftColumn}>
                  <section className={styles.projectSection}>
                    <h2>THE ASK</h2>
                    {editMode ? (
                      <textarea
                        value={draft.description}
                        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                        rows={5}
                        style={{ width: '100%' }}
                      />
                    ) : (
                      <p>{project.description || '—'}</p>
                    )}
                  </section>

                  <section className={`${styles.projectSection} ${styles.mt40}`}>
                    <h2>THE SOLUTION</h2>
                    {editMode ? (
                      <textarea
                        value={draft.solution}
                        onChange={(e) => setDraft({ ...draft, solution: e.target.value })}
                        rows={5}
                        style={{ width: '100%' }}
                      />
                    ) : (
                      <p>{project.solution || '—'}</p>
                    )}
                  </section>

                  {(editMode || (project.keyResults || []).length > 0) && (
                    <section className={`${styles.projectSection} ${styles.mt40}`}>
                      <h2>KEY RESULTS</h2>
                      {editMode ? (
                        <div style={{ display: 'grid', gap: 10 }}>
                          {(draft.keyResults || []).map((r, idx) => (
                            <div key={idx} style={{ display: 'flex', gap: 8 }}>
                              <input
                                value={r}
                                onChange={(e) => updateKeyResult(idx, e.target.value)}
                                placeholder={`Result ${idx + 1}`}
                                style={{ flex: 1 }}
                              />
                              <button onClick={() => removeKeyResult(idx)}>Remove</button>
                            </div>
                          ))}
                          <button onClick={addKeyResult}>+ Add Key Result</button>
                        </div>
                      ) : (
                        <ul>
                          {project.keyResults.map((r, idx) => <li key={idx}>{r}</li>)}
                        </ul>
                      )}
                    </section>
                  )}
                </div>

                <div className={styles.rightColumn}>
                  {isAdmin && editMode && (
                    <section className={styles.projectSection}>
                      <h2>CARD META</h2>
                      <div style={{ display: 'grid', gap: 10 }}>
                        <input
                          placeholder="Client"
                          value={draft.client}
                          onChange={(e) => setDraft({ ...draft, client: e.target.value })}
                        />
                        <input
                          placeholder="Year"
                          value={draft.year}
                          onChange={(e) => setDraft({ ...draft, year: e.target.value })}
                        />
                        <input
                          placeholder="Industry"
                          value={draft.industry}
                          onChange={(e) => setDraft({ ...draft, industry: e.target.value })}
                        />
                        <input
                          placeholder="Timeline"
                          value={draft.timeline}
                          onChange={(e) => setDraft({ ...draft, timeline: e.target.value })}
                        />
                      </div>
                    </section>
                  )}
                  {!editMode && (project.industry || project.timeline) && (
                    <section className={`${styles.projectSection} ${styles.mb40}`}>
                      {project.industry && (
                        <>
                          <h2>INDUSTRY</h2>
                          <p>{project.industry}</p>
                        </>
                      )}
                      {project.timeline && (
                        <>
                          <h2>TIMELINE</h2>
                          <p>{project.timeline}</p>
                        </>
                      )}
                    </section>
                  )}
                  <section className={styles.projectSection}>
                    <h2>DELIVERABLES</h2>
                    {editMode ? (
                      <div style={{ display: 'grid', gap: 10 }}>
                        {(draft.deliverables || []).map((d, idx) => (
                          <div key={idx} style={{ display: 'flex', gap: 8 }}>
                            <input
                              value={d}
                              onChange={(e) => updateDeliverable(idx, e.target.value)}
                              placeholder={`Deliverable ${idx + 1}`}
                              style={{ flex: 1 }}
                            />
                            <button onClick={() => removeDeliverable(idx)}>Remove</button>
                          </div>
                        ))}
                        <button onClick={addDeliverable}>+ Add Deliverable</button>
                      </div>
                    ) : (
                      <ul>
                        {(project.deliverables || []).length ? (
                          project.deliverables.map((d, idx) => <li key={idx}>{d}</li>)
                        ) : (
                          <li>—</li>
                        )}
                      </ul>
                    )}
                  </section>

                  <section className={`${styles.projectSection} ${styles.mt40}`}>
                    <h2>TEAM MEMBERS</h2>
                    {editMode ? (
                      <div style={{ display: 'grid', gap: 10 }}>
                        {(draft.teamMembers || []).map((m, idx) => (
                          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8 }}>
                            <input
                              value={m.fullName}
                              onChange={(e) => updateTeamMember(idx, 'fullName', e.target.value)}
                              placeholder="Full Name"
                            />
                            <input
                              value={m.role}
                              onChange={(e) => updateTeamMember(idx, 'role', e.target.value)}
                              placeholder="Role"
                            />
                            <button onClick={() => removeTeamMember(idx)}>Remove</button>
                          </div>
                        ))}
                        <button onClick={addTeamMember}>+ Add Team Member</button>
                      </div>
                    ) : (
                      <ul>
                        {(project.teamMembers || []).length ? (
                          project.teamMembers.map((m, idx) => <li key={idx}>{m.fullName} - {m.role}</li>)
                        ) : (
                          <li>—</li>
                        )}
                      </ul>
                    )}
                  </section>

                  {isAdmin && (
                    <section className={`${styles.projectSection} ${styles.mt40}`}>
                      <h2>THUMBNAIL</h2>
                      <div style={{ display: 'grid', gap: 10 }}>
                        {project.imageUrl ? (
                          <img alt="thumbnail" src={resolveUrl(project.imageUrl)} style={{ maxWidth: '100%', borderRadius: 8 }} />
                        ) : (
                          <div className={styles.thumbnail}>No thumbnail</div>
                        )}
                        <label style={{ display: 'inline-block' }}>
                          <input type="file" accept="image/*" onChange={(e) => handleThumbnailChange(e.target.files?.[0])} disabled={uploading} />
                        </label>
                      </div>
                    </section>
                  )}
                </div>
              </section>

              <section className={styles.thumbnailsGrid}>
                {(project.visuals && project.visuals.length ? project.visuals : []).map((u, idx) => (
                  <div key={idx} className={styles.thumbnail}>
                    <img src={resolveUrl(u)} alt={`visual-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    {isAdmin && (
                      <button onClick={() => handleRemoveVisual(u)} style={{ position: 'absolute', top: 10, right: 10 }}>Remove</button>
                    )}
                  </div>
                ))}
                {isAdmin && (
                  <div className={styles.thumbnail}>
                    <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                      <span>+ Add Image</span>
                      <input type="file" accept="image/*" onChange={(e) => handleAddVisual(e.target.files?.[0])} disabled={uploading} style={{ display: 'none' }} />
                    </label>
                  </div>
                )}
              </section>

              {isAdmin && (
                <div style={{ marginTop: 16 }}>
                  <label>
                    <span>Add visual: </span>
                    <input type="file" accept="image/*" onChange={(e) => handleAddVisual(e.target.files?.[0])} disabled={uploading} />
                  </label>
                  {uploading && <span style={{ marginLeft: 10 }}>Uploading...</span>}
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProjectTemplate;


